import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Provider, useQuery } from 'urql';

import { messages } from '../../../constants';
import * as D from '../../../deps';
import { useClickControls } from '../../common/AlbumButtonControls';

import AlbumMainContent from './AlbumMainContent';
import AlbumSidebarButtons from './AlbumSidebarButtons';

const getSubTitleLargeLink = (artistUrl, artistName) => {
  return artistUrl ? (
    <Link className={D.HeaderBackstageStyles.SUBTITLE_LINK} to={artistUrl} data-qa={D.QA.HSTSTL}>
      {artistName}
    </Link>
  ) : null;
};

const getSubTitleLarge = (artistUrl, artistName) => {
  return artistName ? (
    <span>
      <FormattedMessage {...messages.albumBy} />
      {getSubTitleLargeLink(artistUrl, artistName)}
    </span>
  ) : null;
};

const getSubTitleSmall = (totalTracks, releaseYear) => {
  if (totalTracks && releaseYear) {
    return (
      <span data-qa={D.QA.HSTST}>
        <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
        {` - ${releaseYear}`}
      </span>
    );
  } else if (totalTracks) {
    return (
      <span data-qa={D.QA.HSTST}>
        <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
      </span>
    );
  } else if (releaseYear) {
    return <span data-qa={D.QA.HSTST}>{releaseYear}</span>;
  }
  return null;
};

const BackstageAlbum = props => {
  const { pandoraId, isMobileWeb, showPremiumAccess, playResumeSource } = props;

  const [{ data, fetching, error }] = useQuery({
    query: D.GraphQL.Queries.getAlbumDetails,
    variables: {
      pandoraId,
    },
  });

  const { playOnClick } = useClickControls({
    pandoraId,
    shareableUrlPath: get(data, 'entity.urlPath', null),
    showPremiumAccess,
    playResumeSource,
  });

  if (fetching) {
    return <D.BackstageLayout2 entityIdentifier={pandoraId} />;
  }
  if (error) {
    return null;
  }

  const albumName = get(data.entity, 'name', '');
  const albumUrlPath = get(data.entity, 'urlPath', null);

  const albumDescription = get(data.entity, 'description', null);
  const isCompilation = get(data.entity, 'isCompilation', false);
  const hasInteractive = get(data.entity, 'rights.hasInteractive', false);

  const releaseDate = get(data.entity, 'releaseDate', null);
  const releaseYear = releaseDate ? new Date(releaseDate).getFullYear() : null;
  const releaseType = get(data.entity, 'releaseType', null);
  const copyright = get(data.entity, 'copyright', null);

  const artistPandoraId = get(data.entity, 'artist.id', null);
  const artistUrl = get(data.entity, 'artist.url', null);
  const artistName = get(data.entity, 'artist.name', null);
  const artistCanSeedStation = get(data.entity, 'artist.canSeedStation', false);

  const tracks = get(data.entity, 'tracks', []);
  const totalTracks = get(data.entity, 'trackCount', 0);

  const artUrl = get(data.entity, 'art.url', null) || D.Utils.Art.getDefaultArt();

  const headerProps = {
    title: albumName,
    titleUrl: albumUrlPath,
    disableHover: isMobileWeb,
    subTitleLrg: getSubTitleLarge(artistUrl, artistName),
    subTitleSm: getSubTitleSmall(totalTracks, releaseYear),
  };

  const mainContentProps = {
    pandoraId,
    copyright,
    albumDescription,
    albumUrlPath,
    isCompilation,
    releaseType,
    tracks,
    disableHover: isMobileWeb,
  };

  const sidebarButtonsProps = {
    pandoraId,
    shareableUrlPath: albumUrlPath,
    hasInteractive,
    artistPandoraId,
    artistName,
    artistHasNoRadio: !artistCanSeedStation,
  };

  const descriptionChildPageProps = {
    description: albumDescription,
    descriptionTitle: <FormattedMessage {...messages.albumDescription} />,
    disableHover: isMobileWeb,
    shareableUrlPath: albumUrlPath,
  };

  return (
    <D.BackstageLayout2
      entityIdentifier={pandoraId}
      heroImageUrl={artUrl}
      heroImageLinkUrl={albumUrlPath}
      headerProps={headerProps}
      sidebarButtons={!isMobileWeb ? <AlbumSidebarButtons {...sidebarButtonsProps} /> : null}
      renderArtworkPlayButton={isMobileWeb}
      mainContent={
        props.children ? (
          React.cloneElement(props.children, descriptionChildPageProps)
        ) : (
          <AlbumMainContent {...mainContentProps} />
        )
      }
      playOnClick={playOnClick}
    />
  );
};

export function mapStateToProps(state) {
  return {
    isMobileWeb: D.isMobileWeb(state),
    showPremiumAccess: D.Utils.Listener.showPremiumAccess(state),
  };
}

const mapDispatchToProps = {
  playResumeSource: D.playResumeSource,
};

export const BackstageAlbumProvider = props => {
  return (
    <Provider value={D.GraphQL.UrqlClient}>
      <BackstageAlbum {...props} />
    </Provider>
  );
};

BackstageAlbum.propTypes = {
  pandoraId: PropTypes.bool,
  isMobileWeb: PropTypes.bool,
  showPremiumAccess: PropTypes.bool,
  playResumeSource: PropTypes.func,
  children: PropTypes.element,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageAlbumProvider);
